"use client";
import { Grid, GridItem } from "@/components/Grid/Grid.component";
import Image from "@/components/Image/Image.component";
import { RichText } from "@/components/RichText/RichText.component";
import { Typography } from "@/components/Typography/Typography.component";
import { useBreakpoints } from "@/hooks/useBreakpoints/useBreakpoints";
import { changeImagePosition, cn } from "@/lib/utils";
import { createRef, useEffect, useMemo } from "react";
import { ImageRefType, ImageStackedPropsModel } from "./ImageStacked.model";

const ImageStacked = ({
  title,
  imageDescription,
  imagesCollection,
  noPadding,
}: ImageStackedPropsModel) => {
  const { isMobile } = useBreakpoints();

  const imagesRef = useMemo(() => {
    const refs: ImageRefType = {};
    imagesCollection.forEach((item, index) => {
      refs[item.sys?.id || index] = createRef();
    });
    return refs;
  }, [imagesCollection]);

  useEffect(() => {
    if (isMobile) {
      for (const [id, imgRef] of Object.entries(imagesRef)) {
        if (imgRef?.current) {
          let image = imagesCollection.find(item => item.sys?.id === id);
          if (!image) {
            image = imagesCollection[Number(id)];
          }
          const { clientWidth: imageWidth } = imgRef.current;
          const { width: originalImageWidth, height: originalImageHeight } =
            image.image;
          const { x: originalFocalPointX } = image?.focalPoint?.focalPoint || {
            x: 0,
            y: 0,
          };
          if (originalImageHeight && originalImageWidth) {
            const imagePosition = changeImagePosition(
              imageWidth,
              originalImageWidth,
              originalFocalPointX,
            );
            if (originalFocalPointX !== undefined) {
              imgRef.current.style.objectPosition = imagePosition;
            }
          }
        }
      }
    }
  }, [imagesRef, isMobile, imagesCollection]);

  return (
    <div
      className={cn(
        "grid gap-y-5 bg-white",
        !noPadding && "px-4 py-10 md:px-10 md:py-20",
      )}
    >
      {(title || imageDescription) && (
        <div className="pb-16">
          {title && <Typography.H4>{title}</Typography.H4>}
          {imageDescription && (
            <div className="w-full max-w-[426px] pt-6">
              <RichText
                additionalClassName={{
                  paragraph: "!text-h7 !leading-[130%]",
                  link: "!text-h7 !leading-[130%]",
                }}
                content={imageDescription.json}
              />
            </div>
          )}
        </div>
      )}
      {imagesCollection.map((image, index) => (
        <Grid key={image.sys?.id || index} hasPadding={false}>
          <GridItem
            span={{
              mobile: 4,
              tablet: image.fullWidth ? 8 : 6,
              desktop: image.fullWidth ? 12 : 6,
            }}
            className={cn(
              "relative w-full place-self-center overflow-hidden",
              image.alignment === "Left" &&
                "md:place-self-start lg:place-self-start",
              image.alignment === "Right" &&
                "md:col-start-3 md:place-self-end lg:col-start-7 lg:place-self-end",
            )}
          >
            <Image
              ref={imagesRef[image.sys?.id || index]}
              src={`${image.image.url}${isMobile ? "?fm=webp&h=638" : "?fm=webp"}`}
              className={cn(
                image.alignment === "Left" && "mr-auto",
                image.alignment === "Right" && "ml-auto",
                image.fullWidth && "w-full",
              )}
              alt={image.image.title || ""}
              width={image.image.width}
              height={image.image.height}
              hasPlaceholder
            />
          </GridItem>
        </Grid>
      ))}
    </div>
  );
};

export default ImageStacked;
